import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Target, Layers, Beaker, ThermometerSun, Droplets, Zap } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const MaterialSpecificSolutions = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const materialSolutions = [
    {
      title: 'Metal Repair Composites',
      description: 'Epoxy-based solutions for rebuilding worn metal surfaces, filling cavities, and restoring dimensions.',
      icon: Layers,
      applications: ['Steel', 'Cast Iron', 'Aluminum', 'Stainless Steel']
    },
    {
      title: 'Ceramic Coatings',
      description: 'High-temperature resistant coatings for extreme thermal environments and abrasion protection.',
      icon: ThermometerSun,
      applications: ['Boilers', 'Furnaces', 'Heat Exchangers', 'Exhaust Systems']
    },
    {
      title: 'Elastomeric Systems',
      description: 'Flexible coatings for substrates subject to vibration, movement, or thermal cycling.',
      icon: Droplets,
      applications: ['Tanks', 'Pipelines', 'Expansion Joints', 'Rubber Linings']
    },
    {
      title: 'Chemical Resistant Barriers',
      description: 'Specialized coatings to protect against aggressive chemicals and corrosive environments.',
      icon: Beaker,
      applications: ['Acid Tanks', 'Chemical Processing', 'Wastewater', 'Battery Rooms']
    }
  ];

  const selectionFactors = [
    'Operating temperature range',
    'Chemical exposure environment',
    'Mechanical stress and loading',
    'Surface preparation requirements',
    'Application method constraints',
    'Cure time and return-to-service needs'
  ];

  const benefits = [
    'Optimized performance for specific conditions',
    'Longer repair life expectancy',
    'Reduced risk of premature failure',
    'Cost-effective material selection',
    'Compliance with industry standards',
    'Technical support and training'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Material-Specific Solutions" description="Material-specific coating and repair solutions for different substrates and environments." path="/maintenance/material-specific-solutions" />
      
      <PageHero
        badge="SMART Program"
        title="Material-Specific Solutions"
        description="The right material for every application. Our engineers match repair solutions to your specific operating conditions for optimal performance and longevity."
        backgroundKey="material-specific-solutions"
        buttonText="Get Material Recommendations"
      />

      {/* Material Solutions */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Material Systems</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {materialSolutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <Card key={index} className="border-t-4 border-t-green-700">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <IconComponent className="text-green-700 mr-3" size={28} />
                      {solution.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{solution.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {solution.applications.map((app, idx) => (
                        <Badge key={idx} variant="outline" className="text-gray-600">
                          {app}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Selection Factors */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">How We Select Materials</h2>
              <p className="text-gray-600 mb-8">
                Our material selection process considers all environmental and operational factors to ensure 
                the best possible repair outcome. We analyze your specific conditions and recommend solutions 
                that will perform reliably for years.
              </p>
              <div className="space-y-3">
                {selectionFactors.map((factor, index) => (
                  <div key={index} className="flex items-center">
                    <Target className="text-green-700 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{factor}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Material Selection Benefits</h3>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-1 flex-shrink-0" size={18} />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Zap className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Need Help Selecting the Right Material?</h2>
          <p className="text-blue-100 mb-8">
            Our engineers will analyze your application and recommend the optimal material solution.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Request Technical Consultation <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MaterialSpecificSolutions;
